import { useEffect, useCallback, useRef, useState } from 'react';
import { useBLSContext } from '../context/BLSContext';
import { useUIMode } from '../context/UIModeContext';
import { useBLSAudio } from '../hooks/useBLSAudio';
import { useFreemium } from '../hooks/useFreemium';
import { useAuth } from '../hooks/useAuth';
import { useZoomApp } from '../hooks/useZoomApp';
import { getZoomAPI } from '../utils/zoomAPI';
import { BLSVisual } from './BLSVisual';
import { TopBar } from './layout/TopBar';
import { BLSPreviewStrip } from './layout/BLSPreviewStrip';
import { SimpleLayout } from './layout/SimpleLayout';
import { AdvancedLayout } from './layout/AdvancedLayout';
import { SpeedControl, speedToHz } from './SpeedControl';
import { StopButton } from './StopButton';
import { TrialBanner } from './TrialBanner';
import { getMixedModeEngine } from '../utils/mixedModeEngine';
import type { SoundName, Direction, BLSState } from '../types';
import { COLOR_PRESETS, PRO_FEATURES, BACKGROUND_COLORS, SOUND_LIBRARY } from '../types';
import { InviteCoachingModal } from './onboarding/InviteCoachingModal';

/**
 * TherapistPanel - Control panel for therapists (hosts)
 *
 * Layout (top → bottom inside the ~360px Zoom sidebar):
 *   TopBar → TrialBanner → scrollable controls → permanent BLSPreviewStrip → sticky footer.
 *
 * The preview strip is always mounted just above the footer. It shrinks from 140px to 80px
 * (compact mode) once a client connects, so the focus shifts from "try it out" to running
 * a real session.
 *
 * Footer state machine:
 *   no client          → [Invite client]                       (Start is structurally absent)
 *   client + idle      → [▶ START] [📨 re-invite (icon)]
 *   client + running   → [⏹ STOP] [📨 re-invite (icon)]
 *
 * Removing Start until a client connects (rather than disabling it) fixes the
 * "Start does nothing when alone" bug at the IA level.
 */

const SESSION_LIMIT = 5;

export function TherapistPanel(): JSX.Element {
  const { state, dispatch } = useBLSContext();
  const { mode } = useUIMode();
  const audio = useBLSAudio();
  const {
    tier,
    isFree,
    isLoading: isLoadingSubscription,
    sessionsRemaining,
    tryCountSession,
  } = useFreemium();
  const { sendMessage, onMessage, inviteAllParticipants, isReady, expandToFullscreen, collapseToSidebar, runningContext, sendToOtherInstances, bringAppToFront, getMeetingParticipantCount } = useZoomApp();
  const { user } = useAuth();

  // Handle upgrade button click
  const handleUpgradeClick = async () => {
    console.log('[TherapistPanel] Creating LemonSqueezy checkout...')
    setIsUpgradeLoading(true);

    try {
      const { data: { session } } = await (await import('../lib/supabase')).supabase.auth.getSession()

      if (!session || !user || !user.email || !user.id) {
        console.error('[TherapistPanel] No active session or user')
        alert('Please sign in first')
        return
      }

      console.log('[TherapistPanel] Creating checkout for:', user.email)

      const { createCheckoutUrl } = await import('../lib/lemonsqueezy')
      const checkoutUrl = await createCheckoutUrl('yearly', user.email, user.id, session.access_token)

      console.log('[TherapistPanel] Opening LemonSqueezy checkout:', checkoutUrl)

      const zoomAPI = getZoomAPI()
      await zoomAPI.openUrl(checkoutUrl)
    } catch (error) {
      console.error('[TherapistPanel] Failed to create checkout:', error)
      alert('Failed to open checkout. Please try again.')
    } finally {
      setIsUpgradeLoading(false);
    }
  }

  // Extract updatePan callback for audio synchronization
  const handlePanUpdate = audio.updatePan;

  // Check if we're in immersive mode
  const isInImmersive = runningContext === 'inImmersive';

  // Track if immersive mode is open (for sidebar toggle)
  const [isImmersiveOpen, setIsImmersiveOpen] = useState(false);

  // Track connected client app instances. Together with participantCount this drives the
  // 3-state footer machine.
  const [connectedClients, setConnectedClients] = useState<Set<string>>(new Set());
  const [, setInviteSent] = useState(false);
  const hasConnectedClients = connectedClients.size > 0;

  // Meeting participant count from the Zoom SDK. null = unknown (treat as DEMO until polled).
  const [participantCount, setParticipantCount] = useState<number | null>(null);

  // Connection mode derivation. "Invite" in this app means "send the EMDR App invitation to
  // a patient who is already in the Zoom call" (sendAppInvitation), NOT "invite to the call."
  //   DEMO     — therapist alone in the call. No one to invite.
  //   INVITE   — patient in call but hasn't added the app yet. Show Invite CTA.
  //   READY    — patient added the app. Show Start / Stop.
  const connectionMode: 'demo' | 'invite' | 'ready' = hasConnectedClients
    ? 'ready'
    : participantCount !== null && participantCount > 1
      ? 'invite'
      : 'demo';

  // Track upgrade checkout loading state
  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false);

  // Tooltip state for info (?) icon in action bar (WCAG SC 1.4.13)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipCloseTimerRef = useRef<number | null>(null);

  // Footer-state aria-live announcement (separate region from any preview-state announcements
  // per accessibility-lead guidance — one live region per semantic concern).
  const [footerAnnouncement, setFooterAnnouncement] = useState('');

  // Refs for focus management when the footer structurally swaps Invite → Start on connect.
  const inviteBtnRef = useRef<HTMLButtonElement>(null);
  const startBtnRef = useRef<HTMLButtonElement>(null);
  // Phase 2 — InviteCoachingModal wiring.
  const reInviteBtnRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Session counting — tracks whether we've already counted a session for this
  // TherapistPanel mount (i.e. this Zoom meeting). Resets when the panel unmounts.
  const hasCountedThisSession = useRef(false);
  const [showSessionLimitModal, setShowSessionLimitModal] = useState(false);

  // Announce state transitions in the footer-state live region. Skip the initial mount so
  // SR isn't pelted with state at load.
  const prevConnectionModeRef = useRef<'demo' | 'invite' | 'ready' | null>(null);
  useEffect(() => {
    const prev = prevConnectionModeRef.current;
    prevConnectionModeRef.current = connectionMode;
    if (prev === null || prev === connectionMode) return;

    let message = '';
    if (prev === 'demo' && connectionMode === 'invite') {
      message = 'Patient joined. You can invite them to the EMDR app.';
    } else if (prev === 'invite' && connectionMode === 'ready') {
      message = 'Patient connected. Start session button available.';
      if (document.activeElement === inviteBtnRef.current) {
        requestAnimationFrame(() => startBtnRef.current?.focus());
      }
    } else if (prev === 'demo' && connectionMode === 'ready') {
      message = 'Patient connected. Start session button available.';
    } else if (connectionMode === 'invite' && prev === 'ready') {
      message = 'Patient disconnected. You can re-invite them to the app.';
    } else if (connectionMode === 'demo') {
      message = 'You\'re alone in the call. Use the demo preview to explore.';
    }
    if (!message) return;

    setFooterAnnouncement('');
    requestAnimationFrame(() => setFooterAnnouncement(message));
  }, [connectionMode]);

  // Use refs to access latest values in message handlers
  const stateRef = useRef(state);
  const runningContextRef = useRef(runningContext);
  const prevIsInImmersiveRef = useRef(isInImmersive);
  const mixedModeEngineRef = useRef(getMixedModeEngine());

  useEffect(() => {
    stateRef.current = state;
    runningContextRef.current = runningContext;
  });

  // Track previous mode for detecting mode switches
  const prevModeRef = useRef(mode);

  // Sync audio state when switching between simple and advanced modes
  useEffect(() => {
    const prevMode = prevModeRef.current;
    prevModeRef.current = mode;

    // simple → advanced: If sound was 'off', disable audio toggle
    if (prevMode === 'simple' && mode === 'advanced') {
      if (state.selectedSounds.includes('off')) {
        dispatch({ type: 'SET_AUDIO_ENABLED', enabled: false });
        dispatch({ type: 'SET_SELECTED_SOUNDS', sounds: ['tap'] }); // Set a default sound
      }
    }

    // advanced → simple: If audio is disabled, set sound to 'off'
    if (prevMode === 'advanced' && mode === 'simple') {
      if (!state.audioEnabled) {
        dispatch({ type: 'SET_SELECTED_SOUNDS', sounds: ['off'] });
      }
    }
  }, [mode, state.selectedSounds, state.audioEnabled, dispatch]);

  // Poll meeting participant count every 5s. When the therapist is alone (count === 1),
  // there can't be any app-connected clients — clear the set so the footer drops back to
  // DEMO. This catches the "patient left the meeting entirely" case where the unmount-
  // cleanup `client_disconnected` may not fire reliably.
  useEffect(() => {
    if (!isReady) return;

    const tick = async () => {
      const count = await getMeetingParticipantCount();
      setParticipantCount(count);
      if (count === 1) {
        setConnectedClients((prev) => (prev.size === 0 ? prev : new Set()));
      }
    };

    tick();
    const intervalId = setInterval(tick, 5000);

    return () => clearInterval(intervalId);
  }, [isReady, getMeetingParticipantCount]);

  // When immersive instance mounts, request current state from sidebar
  useEffect(() => {
    if (isInImmersive && isReady) {
      console.log('[THERAPIST IMMERSIVE] Mounted - requesting current state from sidebar');
      sendToOtherInstances({
        action: 'request_state',
      }).catch((err) => {
        console.warn('[THERAPIST IMMERSIVE] Failed to request state from sidebar:', err);
      });
    }
  }, [isInImmersive, isReady, sendToOtherInstances]);

  // Detect when user closes immersive mode with Zoom's native X button
  useEffect(() => {
    // Only trigger when transitioning FROM immersive (true) TO sidebar (false)
    // Not when both are false (which happens when we first set isImmersiveOpen before context updates)
    const wasInImmersive = prevIsInImmersiveRef.current;
    prevIsInImmersiveRef.current = isInImmersive;

    if (wasInImmersive && !isInImmersive && isImmersiveOpen) {
      console.log('[THERAPIST] Immersive closed by user (Zoom X button) - bringing sidebar to front');
      setIsImmersiveOpen(false);

      // Bring sidebar back to front to prevent app from closing
      bringAppToFront().catch(err => {
        console.warn('[THERAPIST] Failed to bring app to front:', err);
      });
    }
  }, [isInImmersive, isImmersiveOpen, bringAppToFront]);

  // Listen for state requests from immersive instance and client connections
  useEffect(() => {
    if (!isReady) return;

    const unsubscribe = onMessage((message) => {
      const currentContext = runningContextRef.current || 'inMeeting';

      // Handle client connection tracking
      if (message.action === 'client_connected') {
        console.log('[THERAPIST] Client connected:', message.sender);
        setConnectedClients((prev) => {
          const next = new Set(prev);
          next.add(message.sender || 'unknown');
          return next;
        });
      }

      if (message.action === 'client_disconnected') {
        console.log('[THERAPIST] Client disconnected:', message.sender);
        setConnectedClients((prev) => {
          const next = new Set(prev);
          next.delete(message.sender || 'unknown');
          return next;
        });
      }

      // Respond to state request from immersive instance
      if (currentContext === 'inMeeting' && message.action === 'request_state') {
        console.log('[THERAPIST SIDEBAR] Immersive requested state, sending current state:', stateRef.current);
        sendToOtherInstances({
          action: 'update_state',
          params: stateRef.current,
        }).catch((err) => {
          console.warn('[THERAPIST SIDEBAR] Failed to send state to immersive:', err);
        });
      }

      // Apply state updates in immersive mode (existing one-way sync — sidebar pushes,
      // immersive applies). The reverse path (immersive → sidebar Stop) uses the existing
      // client_stopped message which triggers the sidebar's own handleStop.
      if (currentContext === 'inImmersive' && message.action === 'update_state' && message.params) {
        console.log('[THERAPIST IMMERSIVE] Received state update:', message.params);
        dispatch({ type: 'SET_STATE', state: message.params });
      }
    });

    return unsubscribe;
  }, [isReady, onMessage, sendToOtherInstances, dispatch]);

  // Fire the in-app invitation. Called immediately on every Invite click — the modal
  // is coaching shown ALONGSIDE the send, not a confirmation gate.
  const sendInviteDirectly = async () => {
    try {
      await inviteAllParticipants();
      setInviteSent(true);
    } catch (error) {
      console.error('Failed to send invitation:', error);
      alert(`Failed to send invitation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Primary Invite button: send the invite immediately, then open the coaching modal so
  // the therapist can read the scripts aloud while the patient acts on the notification.
  // If the therapist has checked "Don't show this again", the modal is suppressed (invite
  // still fires). Re-invite icon ignores suppression — see handleInviteFromReInviteBtn.
  const handleInviteFromPrimaryBtn = () => {
    openerRef.current = inviteBtnRef.current;
    // Fire-and-forget: kick off the invite and open the modal in the same tick so the
    // coaching overlay is visible while sendAppInvitation resolves in the background.
    void sendInviteDirectly();
    let isSuppressed = false;
    try {
      isSuppressed = localStorage.getItem('emdr_invite_modal_suppressed') === 'true';
    } catch (err) {
      console.warn('[TherapistPanel] Failed to read invite suppression preference:', err);
    }
    if (!isSuppressed) {
      setIsInviteModalOpen(true);
    }
  };

  // Re-invite icon: ALWAYS opens the modal regardless of suppression. Per RESEARCH.md
  // Option 1 (resolving the D-03/D-15 conflict), this is the therapist's built-in path
  // to re-see the coaching modal after suppressing.
  const handleInviteFromReInviteBtn = () => {
    openerRef.current = reInviteBtnRef.current;
    void sendInviteDirectly();
    setIsInviteModalOpen(true);
  };

  // Auto-close the coaching modal the moment the patient's app reaches the waiting state
  // — the coaching is no longer relevant once they're in.
  useEffect(() => {
    if (connectionMode === 'ready' && isInviteModalOpen) {
      setIsInviteModalOpen(false);
    }
  }, [connectionMode, isInviteModalOpen]);

  const handleStart = async () => {
    try {
      // Count session slot for free post-trial users — only when a real client is
      // connected (connectionMode === 'ready'). Demo/invite mode never counts.
      if (isFree && connectionMode === 'ready' && !hasCountedThisSession.current) {
        const result = tryCountSession();
        if (result === 'limit_reached') {
          setShowSessionLimitModal(true);
          return;
        }
        hasCountedThisSession.current = true;
      }

      // Start local preview (only if audio is enabled)
      if (state.audioEnabled) {
        await audio.start(state.selectedSounds, speedToHz(state.speed), state.muteTherapistAudio);
        audio.setVolume(state.volume);
      }
      dispatch({ type: 'START' });

      // Forward to immersive instance if in sidebar mode
      if (!isInImmersive) {
        sendToOtherInstances({
          action: 'update_state',
          params: { ...state, isPlaying: true },
        }).catch(console.error);
      }

      // Broadcast to all participants
      await sendMessage({
        action: 'start_bls',
        params: state,
      });
    } catch (error) {
      console.error('Failed to start BLS:', error);
      alert(`Failed to start BLS: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleStop = useCallback(async () => {
    try {
      audio.stop();
      dispatch({ type: 'STOP' });

      // Best-effort: drop the strip's "Playing in fullscreen" placeholder. If sidebar stops
      // while immersive is open, the immersive view will reflect the paused state on its
      // next render and the user can still dismiss it via Exit Preview.
      setIsImmersiveOpen(false);

      if (runningContext === 'inImmersive') {
        // The sidebar instance has its own AudioEngine + AnimationEngine singletons that
        // are still running. Calling audio.stop() above only stopped the immersive's
        // engines. Send 'client_stopped' — the sidebar already has a handler that runs
        // handleStop in *its* instance, which is the only way to stop the sidebar's
        // engines, dispatch STOP there, and broadcast stop_bls to clients.
        await sendToOtherInstances({
          action: 'client_stopped',
        }).catch((err) => {
          console.warn('[THERAPIST IMMERSIVE] Failed to notify sidebar of stop:', err);
        });
        await collapseToSidebar();
      } else {
        // From sidebar: forward state to immersive (existing flow).
        sendToOtherInstances({
          action: 'update_state',
          params: { isPlaying: false },
        }).catch(console.error);
      }

      await sendMessage({
        action: 'stop_bls',
      });
    } catch (error) {
      console.error('Failed to stop BLS:', error);
    }
  }, [audio, dispatch, sendMessage, sendToOtherInstances, runningContext, collapseToSidebar]);

  // Listen for client-stopped messages
  useEffect(() => {
    if (!isReady) return;

    const unsubscribe = onMessage((message) => {
      if (message.action === 'client_stopped') {
        handleStop();
      }
    });

    return unsubscribe;
  }, [isReady, onMessage, handleStop]);

  // Mixed Mode: Start/stop randomization
  // CRITICAL: Only re-run when isPlaying, mixedMode, or mixedModeConfig changes
  useEffect(() => {
    const mixedEngine = mixedModeEngineRef.current;
    const audioEngine = audio;

    // Check if ANY randomization is enabled (either All Random or individual)
    const isAnyRandomizationEnabled =
      state.mixedMode ||
      Object.values(state.mixedModeConfig).some(v => v === true);

    if (state.isPlaying && isAnyRandomizationEnabled) {
      const proColors = COLOR_PRESETS.map(c => c.value);
      const proBackgrounds = BACKGROUND_COLORS.map(b => b.value);

      // Get all beep sounds (not continuous sounds) for randomization
      const allBeepSounds: SoundName[] = (Object.keys(SOUND_LIBRARY) as SoundName[])
        .filter(soundName => {
          // Only beep sounds, not continuous ones
          return soundName !== 'ocean' && soundName !== 'ambient' && soundName !== 'angelic'
                 && soundName !== 'handpan' && soundName !== 'underwater' && soundName !== 'drone';
        });

      console.log('[MixedMode] 🚀 Starting with:', {
        colors: proColors.length,
        directions: PRO_FEATURES.directions.length,
        styles: PRO_FEATURES.movementStyles.length,
        backgrounds: proBackgrounds.length,
        shapes: PRO_FEATURES.shapes.length,
        sounds: allBeepSounds.length,
        config: state.mixedModeConfig
      });

      mixedEngine.start({
        availableColors: proColors,
        availableDirections: PRO_FEATURES.directions,
        availableMovementStyles: PRO_FEATURES.movementStyles,
        availableBackgrounds: proBackgrounds,
        availableShapes: PRO_FEATURES.shapes,
        availableSounds: allBeepSounds,
        randomizationConfig: state.mixedModeConfig,
        onColorChange: (color) => {
          console.log('[MixedMode] 🎨 Color changed to:', color);
          dispatch({ type: 'SET_COLOR', color });
          if (!runningContextRef.current || runningContextRef.current !== 'inImmersive') {
            sendToOtherInstances({ action: 'update_state', params: { color } }).catch(console.error);
          }
          sendMessage({ action: 'update_state', params: { color } }).catch(console.error);
        },
        onDirectionChange: (direction) => {
          console.log('[MixedMode] 🧭 Direction changed to:', direction);
          dispatch({ type: 'SET_DIRECTION', direction });
          if (!runningContextRef.current || runningContextRef.current !== 'inImmersive') {
            sendToOtherInstances({ action: 'update_state', params: { direction } }).catch(console.error);
          }
          sendMessage({ action: 'update_state', params: { direction } }).catch(console.error);
        },
        onMovementStyleChange: (style) => {
          console.log('[MixedMode] 💫 Movement style changed to:', style);
          dispatch({ type: 'SET_MOVEMENT_STYLE', style });
          if (!runningContextRef.current || runningContextRef.current !== 'inImmersive') {
            sendToOtherInstances({ action: 'update_state', params: { movementStyle: style } }).catch(console.error);
          }
          sendMessage({ action: 'update_state', params: { movementStyle: style } }).catch(console.error);
        },
        onBackgroundChange: (background) => {
          console.log('[MixedMode] 🖼️ Background changed to:', background);
          dispatch({ type: 'SET_BACKGROUND', background });
          if (!runningContextRef.current || runningContextRef.current !== 'inImmersive') {
            sendToOtherInstances({ action: 'update_state', params: { background } }).catch(console.error);
          }
          sendMessage({ action: 'update_state', params: { background } }).catch(console.error);
        },
        onShapeChange: (shape) => {
          console.log('[MixedMode] ⭐ Shape changed to:', shape);
          dispatch({ type: 'SET_SHAPE', shape });
          if (!runningContextRef.current || runningContextRef.current !== 'inImmersive') {
            sendToOtherInstances({ action: 'update_state', params: { shape } }).catch(console.error);
          }
          sendMessage({ action: 'update_state', params: { shape } }).catch(console.error);
        },
      });

      // Only enable audio mixed mode if randomizeSounds is true
      if (state.mixedModeConfig.randomizeSounds) {
        audioEngine.enableMixedMode(allBeepSounds);
      }
    } else {
      mixedEngine.stop();
      audioEngine.disableMixedMode();
    }

    return () => {
      console.log('[MixedMode] 🛑 Cleanup called');
      mixedEngine.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isPlaying, state.mixedMode, state.mixedModeConfig]);

  // Handler functions
  const handleColorChange = (color: string) => {
    dispatch({ type: 'SET_COLOR', color });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { color },
      }).catch(console.error);
    }

    if (state.isPlaying) {
      sendMessage({
        action: 'update_state',
        params: { color },
      }).catch(console.error);
    }
  };

  const handleDirectionChange = (direction: Direction) => {
    dispatch({ type: 'SET_DIRECTION', direction });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { direction },
      }).catch(console.error);
    }

    if (state.isPlaying) {
      sendMessage({
        action: 'update_state',
        params: { direction },
      }).catch(console.error);
    }
  };

  const handleSoundChange = async (sound: SoundName) => {

    let newSounds: SoundName[];
    let isAdding = false;
    let newAudioEnabled = state.audioEnabled; // Track the effective audio enabled state

    if (state.audioMultiSelect) {
      // Multi-select mode: toggle sound in/out of array
      if (state.selectedSounds.includes(sound)) {
        // Deselect: remove from array (keep at least one)
        if (state.selectedSounds.length > 1) {
          newSounds = state.selectedSounds.filter(s => s !== sound);
          isAdding = false;
        } else {
          return; // Don't allow deselecting last sound
        }
      } else {
        // Select: add to array
        newSounds = [...state.selectedSounds, sound];
        isAdding = true;
      }
    } else {
      // Single-select mode: replace array with just this sound
      newSounds = [sound];
      isAdding = true;

      // In simple mode (single-select), sync audioEnabled with 'off' selection
      if (mode === 'simple') {
        if (sound === 'off' && state.audioEnabled) {
          dispatch({ type: 'SET_AUDIO_ENABLED', enabled: false });
          newAudioEnabled = false;
        } else if (sound !== 'off' && !state.audioEnabled) {
          dispatch({ type: 'SET_AUDIO_ENABLED', enabled: true });
          newAudioEnabled = true;
        }
      }
    }

    dispatch({ type: 'SET_SELECTED_SOUNDS', sounds: newSounds });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { selectedSounds: newSounds, audioEnabled: newAudioEnabled },
      }).catch(console.error);
    }

    // If playing, update audio
    if (state.isPlaying) {
      if (state.audioMultiSelect && newAudioEnabled) {
        // Multi-select mode: incrementally add/remove without affecting others
        if (isAdding) {
          await audio.addSound(sound);
        } else {
          audio.removeSound(sound);
        }
      } else if (!state.audioMultiSelect) {
        // Single-select mode: handle sound changes including 'off'
        audio.stop();
        if (newAudioEnabled && sound !== 'off') {
          await new Promise(resolve => setTimeout(resolve, 50));
          await audio.start(newSounds, speedToHz(state.speed), state.muteTherapistAudio);
        }
      }

      sendMessage({
        action: 'update_state',
        params: { selectedSounds: newSounds, audioEnabled: newAudioEnabled },
      }).catch(console.error);
    }
  };

  const handleSpeedChange = async (speed: number) => {
    dispatch({ type: 'SET_SPEED', speed });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { speed },
      }).catch(console.error);
    }

    if (state.isPlaying && state.audioEnabled) {
      audio.stop();
      await new Promise(resolve => setTimeout(resolve, 50));
      await audio.start(state.selectedSounds, speedToHz(speed), state.muteTherapistAudio);

      sendMessage({
        action: 'update_state',
        params: { speed },
      }).catch(console.error);
    }
  };

  const handleVolumeChange = (volume: number) => {
    dispatch({ type: 'SET_VOLUME', volume });
    audio.setVolume(volume);

    if (state.isPlaying) {
      sendMessage({
        action: 'update_state',
        params: { volume },
      }).catch(console.error);
    }
  };

  const handleMuteChange = async (muted: boolean) => {
    dispatch({ type: 'SET_MUTE_THERAPIST_AUDIO', muted });

    if (state.isPlaying && state.audioEnabled) {
      audio.stop();
      await new Promise(resolve => setTimeout(resolve, 50));
      await audio.start(state.selectedSounds, speedToHz(state.speed), muted);
    }
  };

  const handleBackgroundChange = (background: string) => {
    dispatch({ type: 'SET_BACKGROUND', background });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { background },
      }).catch(console.error);
    }

    if (state.isPlaying) {
      sendMessage({
        action: 'update_state',
        params: { background },
      }).catch(console.error);
    }
  };

  const handleMovementStyleChange = (style: BLSState['movementStyle']) => {
    dispatch({ type: 'SET_MOVEMENT_STYLE', style });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { movementStyle: style },
      }).catch(console.error);
    }

    if (state.isPlaying) {
      sendMessage({
        action: 'update_state',
        params: { movementStyle: style },
      }).catch(console.error);
    }
  };

  const handleMixedModeChange = (enabled: boolean) => {
    dispatch({ type: 'SET_MIXED_MODE', enabled });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { mixedMode: enabled },
      }).catch(console.error);
    }

    sendMessage({
      action: 'update_state',
      params: { mixedMode: enabled },
    }).catch(console.error);
  };

  const handleToggleAllRandom = (enabled: boolean) => {
    dispatch({ type: 'TOGGLE_ALL_RANDOM', enabled });

    // Sync to immersive and broadcast
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: {
          mixedMode: enabled,
          mixedModeConfig: {
            randomizeColor: enabled,
            randomizeDirection: enabled,
            randomizeMovementStyle: enabled,
            randomizeBackground: enabled,
            randomizeShape: enabled,
            randomizeSounds: enabled,
          },
        },
      }).catch(console.error);
    }
    sendMessage({
      action: 'update_state',
      params: {
        mixedMode: enabled,
        mixedModeConfig: {
          randomizeColor: enabled,
          randomizeDirection: enabled,
          randomizeMovementStyle: enabled,
          randomizeBackground: enabled,
          randomizeShape: enabled,
          randomizeSounds: enabled,
        },
      },
    }).catch(console.error);
  };

  const handleMixedModeConfigChange = (config: Partial<BLSState['mixedModeConfig']>) => {
    dispatch({ type: 'SET_MIXED_MODE_CONFIG', config });

    // Sync to immersive and broadcast
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { mixedModeConfig: { ...state.mixedModeConfig, ...config } },
      }).catch(console.error);
    }
    sendMessage({
      action: 'update_state',
      params: { mixedModeConfig: { ...state.mixedModeConfig, ...config } },
    }).catch(console.error);
  };

  const handleShapeChange = (shape: BLSState['shape']) => {
    dispatch({ type: 'SET_SHAPE', shape });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { shape },
      }).catch(console.error);
    }

    if (state.isPlaying) {
      sendMessage({
        action: 'update_state',
        params: { shape },
      }).catch(console.error);
    }
  };

  const handleVisualEnabledChange = (enabled: boolean) => {
    dispatch({ type: 'SET_VISUAL_ENABLED', enabled });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { visualEnabled: enabled },
      }).catch(console.error);
    }

    if (state.isPlaying) {
      sendMessage({
        action: 'update_state',
        params: { visualEnabled: enabled },
      }).catch(console.error);
    }
  };

  const handleAudioEnabledChange = async (enabled: boolean) => {
    dispatch({ type: 'SET_AUDIO_ENABLED', enabled });

    // Forward to immersive instance if in sidebar mode
    if (!isInImmersive) {
      sendToOtherInstances({
        action: 'update_state',
        params: { audioEnabled: enabled },
      }).catch(console.error);
    }

    if (state.isPlaying) {
      if (enabled) {
        // Re-enable audio: start it
        await audio.start(state.selectedSounds, speedToHz(state.speed), state.muteTherapistAudio);
        audio.setVolume(state.volume);
      } else {
        // Disable audio: stop it
        audio.stop();
      }

      sendMessage({
        action: 'update_state',
        params: { audioEnabled: enabled },
      }).catch(console.error);
    }
  };

  const handleMultiSelectChange = (enabled: boolean) => {
    dispatch({ type: 'SET_AUDIO_MULTI_SELECT', enabled });

    // When switching to single-select, keep only first sound
    if (!enabled && state.selectedSounds.length > 1) {
      dispatch({ type: 'SET_SELECTED_SOUNDS', sounds: [state.selectedSounds[0]] });
    }
  };

  const handleExpandPreview = async () => {
    try {
      // Toggle: if immersive is open (from sidebar or in immersive), collapse; otherwise expand
      if (isImmersiveOpen || isInImmersive) {
        setIsImmersiveOpen(false);
        await collapseToSidebar();
        return;
      }

      // Auto-start the demo when expanding in DEMO mode so the immersive view actually animates.
      // No broadcast happens (participantCount === 1), so this is purely a local "play".
      const shouldAutoStart = connectionMode === 'demo' && !state.isPlaying;

      setIsImmersiveOpen(true);
      await expandToFullscreen();

      if (shouldAutoStart) {
        await handleStart();
      }
    } catch (error) {
      console.error('Failed to toggle preview:', error);
    }
  };

  const handleCollapsePreview = async () => {
    try {
      setIsImmersiveOpen(false);
      await collapseToSidebar();
    } catch (error) {
      console.error('Failed to collapse:', error);
    }
  };

  // Announce live-session start/stop in a polite region. Separate concern from footer
  // connection announcements (per accessibility-lead: one live region per concern).
  const [sessionAnnouncement, setSessionAnnouncement] = useState('');
  const prevIsPlayingRef = useRef(state.isPlaying);
  useEffect(() => {
    const prev = prevIsPlayingRef.current;
    prevIsPlayingRef.current = state.isPlaying;
    if (prev === state.isPlaying) return;
    setSessionAnnouncement('');
    requestAnimationFrame(() => {
      setSessionAnnouncement(state.isPlaying ? 'BLS session started.' : 'BLS session stopped.');
    });
  }, [state.isPlaying]);

  // Immersive preview mode - show full BLS visual
  if (isInImmersive) {
    const immersiveAnnouncement = state.isPlaying
      ? 'Demo preview playing in fullscreen. Press Escape or use Exit Preview to return.'
      : 'Fullscreen preview opened. Animation paused.';
    return (
      <div
        className="relative w-screen h-screen overflow-hidden flex items-center justify-center"
        style={{ backgroundColor: state.background }}
      >
        {/* SR orientation on immersive entry — auto-start is non-obvious behavior. */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {immersiveAnnouncement}
        </div>
        {/* BLS Visual - fullscreen (when visual enabled) */}
        {state.visualEnabled && (
          <BLSVisual
            color={state.color}
            direction={state.direction}
            speed={state.speed}
            isPlaying={state.isPlaying}
            movementStyle={state.movementStyle}
            shape={state.shape}
            onPanUpdate={handlePanUpdate}
          />
        )}

        {/* Invisible animation for audio-only mode (provides pan updates) */}
        {!state.visualEnabled && state.audioEnabled && (
          <div className="absolute inset-0 opacity-0 pointer-events-none">
            <BLSVisual
              color={state.color}
              direction={state.direction}
              speed={state.speed}
              isPlaying={true}
              movementStyle={state.movementStyle}
              shape={state.shape}
              onPanUpdate={handlePanUpdate}
            />
          </div>
        )}

        {/* Audio-only message (when visual disabled) */}
        {!state.visualEnabled && state.audioEnabled && (
          <div className="text-center space-y-4 p-8 z-10">
            <div className="text-8xl mb-4">🎧</div>
            <h1 className="text-4xl font-bold text-gray-900">
              Audio BLS Active
            </h1>
            <p className="text-gray-600 text-xl">
              Bilateral audio stimulation in progress
            </p>

            {/* Audio indicator */}
            <div className="flex items-center justify-center gap-2 mt-8 bg-white rounded-full px-6 py-3 shadow-lg">
              <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-base font-medium text-gray-700">Playing</span>
            </div>
          </div>
        )}

        {/* Session not started message (when both disabled) */}
        {!state.visualEnabled && !state.audioEnabled && (
          <div className="text-center space-y-4 p-8 z-10">
            <div className="text-8xl mb-4">👁️</div>
            <h1 className="text-4xl font-bold text-gray-900">
              Immersive Preview
            </h1>
            <p className="text-gray-600 text-xl">
              This is what your patient sees in immersive mode
            </p>
          </div>
        )}

        {/* Exit Preview button - only show when not playing (same position as Stop button) */}
        {!state.isPlaying && (
          <button
            onClick={handleCollapsePreview}
            className="
              absolute top-4 left-4 z-50
              bg-gray-100 hover:bg-gray-200 active:bg-gray-300
              text-gray-500 font-semibold
              px-6 py-3 rounded-lg
              shadow-2xl hover:shadow-gray-500/50
              transition-all duration-200
              flex items-center gap-2
              text-lg
            "
            aria-label="Exit preview mode"
          >
            <span className="text-xl">✕</span>
            <span>Exit Preview</span>
          </button>
        )}

        {/* Stop button (if playing) */}
        {state.isPlaying && <StopButton onStop={handleStop} />}
      </div>
    );
  }

  // Show loading state while subscription is being checked
  // This prevents the free→pro flash
  if (isLoadingSubscription) {
    return (
      <div className="loading-subscription">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-200 border-t-teal-600 mx-auto"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Normal therapist panel (sidebar mode)
  return (
    <div className="flex h-screen bg-app-bg">
      {/* Controls Panel */}
      <div className="w-full bg-surface shadow-lg flex flex-col h-full">
        {/* Controls + preview region — wrapper is positioned + flex-1 so the coaching
            modal (absolute inset-0 inside the wrapper) covers the scrollable controls
            AND the preview strip. The action bar footer below is OUTSIDE the wrapper
            and therefore always visible. No measurement needed: flex layout owns the
            geometry. */}
        <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-6 space-y-6">
          {/* Top Bar */}
          <TopBar />

          {/* Session counter card — free post-trial users only */}
          {isFree && (() => {
            const isEmpty        = sessionsRemaining === 0;
            const isLow          = sessionsRemaining <= 2 && !isEmpty;
            // "Just used last" = they're currently in a session that consumed the last slot
            const isJustUsedLast = isEmpty && hasCountedThisSession.current;
            const pct            = ((SESSION_LIMIT - sessionsRemaining) / SESSION_LIMIT) * 100;

            // Card: red only when they open the app already empty (no active session)
            const cardBg    = (isEmpty && !isJustUsedLast) ? 'bg-red-50 border-red-200'
                            : (isLow || isJustUsedLast)    ? 'bg-orange-50 border-orange-200'
                            :                                 'bg-blue-50 border-blue-100';
            const barTrack  = (isEmpty && !isJustUsedLast) ? 'bg-red-100'   : (isLow || isJustUsedLast) ? 'bg-orange-100'   : 'bg-blue-100';
            const barFill   = (isEmpty && !isJustUsedLast) ? 'bg-red-400'   : (isLow || isJustUsedLast) ? 'bg-orange-500'   : 'bg-sync-blue';
            const labelColor= (isEmpty && !isJustUsedLast) ? 'text-red-700' : (isLow || isJustUsedLast) ? 'text-orange-800' : 'text-gray-800';

            const label = isJustUsedLast
              ? 'Last free session of the month.'
              : isEmpty
                ? 'No sessions left this month'
                : `${sessionsRemaining} of ${SESSION_LIMIT} sessions left this month`;

            return (
              <div className={`rounded-xl border px-4 py-3 ${cardBg}`}>
                {/* Label — full width */}
                <p className={`text-sm font-semibold mb-2 ${labelColor}`}>{label}</p>

                {/* Bar */}
                <div className={`w-full h-2 rounded-full overflow-hidden ${barTrack}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-700 ease-out ${barFill}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>

                {/* CTA — always blue, own row */}
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleUpgradeClick}
                    disabled={isUpgradeLoading}
                    className="text-xs font-bold px-3 py-1.5 rounded-full transition-all shadow-sm disabled:opacity-60 flex items-center gap-1.5 min-w-[130px] justify-center bg-sync-blue hover:bg-cta-hover text-white"
                  >
                    {isUpgradeLoading
                      ? <><div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent flex-shrink-0" /><span>Opening...</span></>
                      : <span>⚡ Unlock Unlimited</span>}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Trial Banner - Only shown for free (non-authenticated) users */}
          <TrialBanner />

          {/* Error Message */}
          {audio.loadError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              <strong>Audio Error:</strong> {audio.loadError}
            </div>
          )}

          {/* Conditional Layout based on mode */}
          {mode === 'simple' ? (
            <SimpleLayout
              color={state.color}
              direction={state.direction}
              sound={state.selectedSounds[0] || 'tap'}
              isPro={true}
              onColorChange={handleColorChange}
              onDirectionChange={handleDirectionChange}
              onSoundChange={handleSoundChange}
            />
          ) : (
            <AdvancedLayout
              state={state}
              tier={tier}
              isPro={true}
              onColorChange={handleColorChange}
              onDirectionChange={handleDirectionChange}
              onSoundChange={handleSoundChange}
              onVolumeChange={handleVolumeChange}
              onMuteChange={handleMuteChange}
              onMultiSelectChange={handleMultiSelectChange}
              onBackgroundChange={handleBackgroundChange}
              onMovementStyleChange={handleMovementStyleChange}
              onShapeChange={handleShapeChange}
              onVisualEnabledChange={handleVisualEnabledChange}
              onAudioEnabledChange={handleAudioEnabledChange}
              onToggleAllRandom={handleToggleAllRandom}
              onMixedModeConfigChange={handleMixedModeConfigChange}
              onMixedModeChange={handleMixedModeChange}
              canUseMixedModeConfig={() => true}
            />
          )}

          {/* Speed Control - Standalone between Audio and Preview */}
          <SpeedControl
            value={state.speed}
            onChange={handleSpeedChange}
            disabled={false}
            tier='pro'
          />

          </div>
        </div>

        {/* Preview strip — last item in the controls flex-column, pinned just below
            the scrollable region. Modal (below) covers this too so the coaching
            overlay isn't punctured by an animated ball. */}
        <BLSPreviewStrip
          color={state.color}
          direction={state.direction}
          speed={state.speed}
          movementStyle={state.movementStyle}
          shape={state.shape}
          background={state.background}
          isPlaying={state.isPlaying}
          connectionMode={connectionMode}
          isImmersiveOpen={isImmersiveOpen}
          onPanUpdate={handlePanUpdate}
          onExpandToFullscreen={handleExpandPreview}
        />
        </div>

        {/* Session limit modal — shown when free user has used all 5 monthly sessions */}
        {showSessionLimitModal && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 rounded-lg">
            <div className="bg-white rounded-xl p-6 mx-4 shadow-2xl max-w-xs w-full">
              <p className="text-3xl text-center mb-3">🔒</p>
              <h3 className="text-base font-semibold text-center text-gray-900 mb-2">
                Monthly sessions used up
              </h3>
              <p className="text-sm text-gray-600 text-center mb-5">
                You've used your 5 free sessions this month. They reset on the 1st of next month, or upgrade for unlimited sessions.
              </p>
              <button
                onClick={handleUpgradeClick}
                disabled={isUpgradeLoading}
                className="w-full bg-sync-blue hover:bg-cta-hover disabled:opacity-60 text-white font-medium py-2.5 rounded-lg text-sm transition-colors mb-2 flex items-center justify-center gap-2"
              >
                {isUpgradeLoading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                {isUpgradeLoading ? 'Opening checkout...' : 'Upgrade for unlimited sessions'}
              </button>
              <button
                onClick={() => setShowSessionLimitModal(false)}
                className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Phase 2 — Guided Invite Modal. Coaching overlay shown after the invite already
            fired; therapist reads the scripts aloud while the patient acts. Auto-closes
            when the patient connects (connectionMode → 'ready'). absolute inset-0 inside
            the wrapper means it covers controls + preview strip; the action bar footer
            below the wrapper stays visible. */}
        {isInviteModalOpen && (
          <InviteCoachingModal
            onClose={() => {
              setIsInviteModalOpen(false);
            }}
            openerElement={openerRef.current}
          />
        )}
        </div>

        {/* Sticky Action Bar — content swaps based on the 3-state connection machine. */}
        <footer className="px-4 py-3 bg-white border-t border-gray-200 flex-shrink-0">
          {connectionMode === 'demo' ? (
            // DEMO state: no patient in the call. Play the BLS preview locally so the
            // therapist can explore the tool. Expanding the strip to fullscreen also
            // auto-starts the demo (handleExpandPreview handles that).
            <div className="flex flex-col gap-1.5">
              <p className="text-xs text-text-muted text-center">
                No patient yet — try the animation. Nothing is broadcast.
              </p>
              <button
                type="button"
                onClick={state.isPlaying ? handleStop : handleStart}
                disabled={!audio.isLoaded || !isReady}
                aria-label={state.isPlaying ? 'Stop BLS preview' : 'Play BLS preview'}
                className={`w-full min-h-[44px] py-3 rounded-lg font-semibold text-base transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                  state.isPlaying
                    ? 'bg-emdr-navy text-white hover:bg-emdr-navy/90 focus-visible:ring-emdr-navy'
                    : 'bg-sync-blue text-white hover:bg-cta-hover focus-visible:ring-sync-blue'
                }`}
              >
                {!audio.isLoaded ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Loading...
                  </span>
                ) : state.isPlaying ? (
                  <><span aria-hidden="true">⏹</span> Stop BLS preview</>
                ) : (
                  <><span aria-hidden="true">▶</span> Play BLS preview</>
                )}
              </button>
            </div>
          ) : connectionMode === 'invite' ? (
            // INVITE state: patient is in the call but hasn't added the EMDR app yet.
            isFree && sessionsRemaining === 0 ? (
              // No sessions left — block invite, show upgrade prompt instead
              <div className="flex flex-col gap-2">
                <p className="text-xs text-center text-gray-500">
                  No free sessions left this month. Upgrade to invite patients.
                </p>
                <button
                  onClick={handleUpgradeClick}
                  disabled={isUpgradeLoading}
                  className="w-full min-h-[44px] py-3 rounded-lg font-semibold text-base transition-colors bg-sync-blue hover:bg-cta-hover text-white shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isUpgradeLoading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                  ⚡ Unlock Unlimited Sessions
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <p id="invite-helper-text" className="text-xs text-text-muted text-center">
                  A patient joined the call. Send them the EMDR app invitation to begin.
                </p>
                <button
                  ref={inviteBtnRef}
                  type="button"
                  onClick={handleInviteFromPrimaryBtn}
                  disabled={!isReady}
                  aria-describedby="invite-helper-text"
                  className="w-full min-h-[44px] py-3 rounded-lg font-semibold text-base transition-colors bg-sync-blue hover:bg-cta-hover text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sync-blue focus-visible:ring-offset-2"
                >
                  <span aria-hidden="true">📨</span> Invite patient to app
                </button>
              </div>
            )
          ) : (
            // READY: Start/Stop primary + 44×44 re-invite icon. The re-invite icon itself
            // shows a visual tooltip on hover/focus — no separate (?) disclosure (the action
            // button's aria-label already conveys purpose to AT, so the tooltip is purely a
            // sighted-only progressive disclosure).
            <div className="flex items-center gap-2">
              {!state.isPlaying ? (
                <button
                  ref={startBtnRef}
                  type="button"
                  onClick={handleStart}
                  disabled={!audio.isLoaded || !isReady}
                  aria-label="Start BLS session"
                  className="flex-1 min-h-[44px] py-3 rounded-lg font-semibold text-base transition-colors bg-sync-blue hover:bg-cta-hover text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sync-blue focus-visible:ring-offset-2"
                >
                  {!audio.isLoaded ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Loading...
                    </span>
                  ) : (
                    <><span aria-hidden="true">▶</span> Start BLS</>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleStop}
                  aria-label="Stop BLS session (currently running)"
                  className="flex-1 min-h-[44px] py-3 rounded-lg font-semibold text-base transition-colors bg-red-600 hover:bg-red-700 text-white breathing-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
                >
                  <span aria-hidden="true">⏹</span> Stop BLS
                </button>
              )}

              {/* Icon-only re-invite — 44×44 + sighted-only visual tooltip on hover/focus.
                  SR users get the same info via the button's aria-label, so no aria-describedby
                  (avoids the verbose double-announcement). 300ms close delay + mirrored handlers
                  on the popover so the pointer can travel onto it (SC 1.4.13 Hoverable). */}
              <div className="relative">
                <button
                  ref={reInviteBtnRef}
                  type="button"
                  onClick={handleInviteFromReInviteBtn}
                  disabled={!isReady}
                  aria-label="Re-invite patient (e.g., if they closed the app)"
                  className="min-h-[44px] min-w-[44px] aspect-square flex items-center justify-center rounded-lg font-semibold transition-colors bg-sync-blue/10 text-sync-blue hover:bg-sync-blue/20 active:bg-sync-blue/30 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sync-blue focus-visible:ring-offset-2"
                  onFocus={() => setIsTooltipOpen(true)}
                  onBlur={() => setIsTooltipOpen(false)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setIsTooltipOpen(false); }}
                  onMouseEnter={() => {
                    if (tooltipCloseTimerRef.current !== null) {
                      clearTimeout(tooltipCloseTimerRef.current);
                      tooltipCloseTimerRef.current = null;
                    }
                    setIsTooltipOpen(true);
                  }}
                  onMouseLeave={() => {
                    tooltipCloseTimerRef.current = window.setTimeout(() => setIsTooltipOpen(false), 300);
                  }}
                >
                  <span aria-hidden="true">📨</span>
                </button>

                {isTooltipOpen && (
                  <div
                    aria-hidden="true"
                    className="absolute bottom-full right-0 mb-2 bg-emdr-navy text-white text-sm px-3 py-2 rounded-md shadow-lg max-w-[280px] z-50"
                    onMouseEnter={() => {
                      if (tooltipCloseTimerRef.current !== null) {
                        clearTimeout(tooltipCloseTimerRef.current);
                        tooltipCloseTimerRef.current = null;
                      }
                      setIsTooltipOpen(true);
                    }}
                    onMouseLeave={() => {
                      tooltipCloseTimerRef.current = window.setTimeout(() => setIsTooltipOpen(false), 300);
                    }}
                  >
                    Re-invite if the patient closed the Zoom App. They'll see the notification again.
                  </div>
                )}
              </div>
            </div>
          )}
        </footer>

        {/* Footer-state aria-live region (connection events). */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {footerAnnouncement}
        </div>
        {/* Session-state aria-live region (BLS start/stop). Separate region per concern. */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {sessionAnnouncement}
        </div>
      </div>
    </div>
  );
}
